import { OwnershipLabelsProvider } from "../kyc/OwnershipLabels";
// KycReviewPage.jsx

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { normalizeRole, isReadOnlyArea, PROTECTED_AREAS } from "../auth/permissions";
import { appConfirm } from "../components/ui/appConfirm";

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "../firebase";

import "./css/KycClientSideFormPage.css";
import "./css/KycReviewPage.css";
import { useAppLoading } from "../context/AppLoadingContext";
import { MAIL_API_BASE } from "../config/api";
import iconMailboxD from "../images/mailbox-D.svg";
import iconReturnWhite from "../images/return-white.svg";
import iconThumbsDownB from "../images/thumbs-down-B.svg";
import iconThumbsUpB from "../images/thumbs-up-B.svg";
import iconTrashBin from "../images/trash-bin.svg";

// ✅ Shared modular sections
import KycSectionCustomerDetails from "../kyc/sections/common/KycSectionCustomerDetails";
import KycReviewOwnershipSection, {
  buildReviewOwnershipApiPayload,
  buildLiveReviewOwnershipSnapshot,
} from "../kyc/sections/review/KycReviewOwnershipSection";
import KycClientSignatoriesDesktop from "../kyc/sections/client/KycClientSignatoriesDesktop";
import KycSectionSowSof from "../kyc/sections/common/KycSectionSowSof";
import KycSectionPepSanctions from "../kyc/sections/common/KycSectionPepSanctions";
import {
  normalizeClientKycData,
  normalizeOwnershipData,
  normalizePepSanctionsData,
  removeUndefinedDeep,
  toKycDateOrNull,
} from "../utils/clientKycPayload";
import {
  assessUbosFromTree,
  applyUboAssessmentResultToOwnership,
} from "../utils/uboAssessment";
import { useLiveUboAssessment } from "../kyc/hooks/useLiveUboAssessment";
import useNameEntryMode from "../utils/useNameEntryMode";

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

// ---------------------------
// Helpers
// ---------------------------
function isPlainObject(x) {
  return x && typeof x === "object" && !Array.isArray(x);
}

function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }

  if (isPlainObject(a) || isPlainObject(b)) {
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
      if (!deepEqual(a[k], b[k])) return false;
    }
    return true;
  }

  return false;
}

function getAt(obj, path) {
  let ref = obj;
  for (const p of path) {
    if (ref == null) return undefined;
    ref = ref[p];
  }
  return ref;
}

function toDateOnlyKey(value) {
  const date = toKycDateOrNull(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeCustomerForCompare(customer) {
  const c = customer || {};
  return {
    ...c,
    dobOrIncorporationDate: toDateOnlyKey(c.dobOrIncorporationDate),
  };
}

// ✅ baseline compare subset (what client can edit)
function pickClientEditable(d) {
  const x = d || {};
  return {
    customer: normalizeCustomerForCompare(x.customer || {}),
    uaeCompany: x.uaeCompany || {},
    ownership: normalizeOwnershipData(x.ownership || {}),
    sourceOfFundsWealth: x.sourceOfFundsWealth || {},
    pepSanctions: normalizePepSanctionsData(x.pepSanctions || {}),
    engagement: x.engagement || {},
    documents: x.documents || {},
  };
}

// joinURL
function joinUrl(base, path) {
  return (
    String(base || "").replace(/\/+$/, "") +
    "/" +
    String(path || "").replace(/^\/+/, "")
  );
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

const SERVER_URL = process.env.REACT_APP_SERVER_URL || MAIL_API_BASE;

const ORG_GEO_PREFERRED_COUNTRY = {
  uae: "AE",
  switzerland: "CH",
};

function orgGeoToPreferredCountry(value) {
  return ORG_GEO_PREFERRED_COUNTRY[String(value || "").trim().toLowerCase()] || "";
}

// -------------------------------------------------------
// -----------------   EXPORT FUNCTION   -----------------
// -------------------------------------------------------
export default function KycReviewPage({ submissionId: submissionIdProp = null, onClose = null, isModal = false, onSuccess = null }) {
  const { submissionId: submissionIdParam } = useParams();
  const submissionId = submissionIdProp || submissionIdParam;
  const nav = useNavigate();

  const location = useLocation();
  const from = location.state?.from; // "inbox" | "clients"
  const backLabel = from === "inbox" ? "Back to Inbox" : "Back";
  const backTo = from === "inbox" ? "/kyc-inbox" : "/clients";

  // ✅ user profile state (org restriction)
  const [meLoading, setMeLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [myOrgId, setMyOrgId] = useState("");
  // Fail closed until the profile is loaded (see src/auth/permissions.js).
  const [myRole, setMyRole] = useState("");
  const [myTeamId, setMyTeamId] = useState("");

  const [loading, setLoading] = useState(true);

  const { showOverlay, hideOverlay } = useAppLoading();
  useLayoutEffect(() => {
    if (isModal) return;
    if (loading || meLoading) {
      showOverlay({ title: "Loading…", subtitle: "", loaderStyle: "dots", bg: "#EDF1F4" });
      return () => hideOverlay();
    }
  }, [isModal, loading, meLoading, showOverlay, hideOverlay]);
  const [saving, setSaving] = useState(false);

  // ✅ split errors: loadErr = fatal, err = non-fatal
  const [loadErr, setLoadErr] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const clearMsgOnEdit = () => setMsg("");

  const [submission, setSubmission] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientType, setClientType] = useState("");
  const [orgPreferredPhoneCountry, setOrgPreferredPhoneCountry] = useState("");
  const [orgJurisdiction, setOrgJurisdiction] = useState(null);

  // ✅ staff baseline (kyc/{clientId}) used for change highlighting
  const [staffBase, setStaffBase] = useState(null);

  // ✅ unified KYC form state (client submission data editable by staff before approving)
  const [form, setForm] = useState({
    engagement: {
      clientRequests: [],
      approach: {
        channel: "Direct",
        introducerName: "",
        paidIntroducer: false,
      },
    },
    customer: {
      type: "individual",
      name: "",
      nationalityOrIncorporationCountry: "",
      dobOrIncorporationDate: "",
      address: "",
      city: "",
      country: "",
      website: "",
      businessActivity: "",
    },
    uaeCompany: {
      proposedEntityNames: "",
      jurisdiction: "Mainland",
      activities: "",
      expectedMonthlyTurnoverRange: "",
      expectedCustomerCountries: [],
      expectedSupplierCountries: [],
      expectedTransactionChannels: [],
      highRiskCountries: [],
    },
    ownership: { shareholders: [], ubos: [], controllers: [] },
    sourceOfFundsWealth: {
      sourceOfFunds: "",
      sourceOfFundsDetails: "",
      sourceOfWealth: [],
      sourceOfWealthDetails: "",
      supportingDocuments: [],
      netWorthCurrency: "USD",
      netWorthRange: "",
      annualIncomeCurrency: "USD",
      annualIncomeRange: "",
      annualTurnoverCurrency: "USD",
      annualTurnoverRange: "",
      sourceOfFundsTypes: [],
      thirdPartyDetails: "",
      sourceOfFundsCashDetails: "",
      sourceOfFundsOtherDetails: "",
    },
    pepSanctions: {
      pepDeclared: false,
      pepDetails: "",
      sanctionsDeclared: false,
      sanctionsDetails: "",
    },
    documents: {
      individualDocs: [],
      corporateDocs: [],
      missingDocs: [],
      notes: "",
    },
    internalUse: {
      riskRating: "Medium",
      eddRequired: false,
      eddRationale: "",
      screeningSummary: "",
      goAmlFiled: false,
      goAmlReference: "",
    },
    clientComment: "",
  });

  // ✅ dynamic section numbering (Ownership exists only for entities)

  const customerType = form?.customer?.type || "individual";
  const isEntity = customerType === "entity";

  const sectionNo = useMemo(() => {
    let n = 1;
    const out = {};
    out.customerDetails = n++; // 1
    out.ownership = isEntity ? n++ : null; // 2 (only if entity)
    out.signatories = isEntity ? n++ : null; // 3 (only if entity)
    out.sowSof = n++; // 2 or 4
    out.pepSanctions = n++; // 3 or 5
    return out;
  }, [isEntity]);

  function setAt(path, value) {
    clearMsgOnEdit();
    setForm((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = value;
      return next;
    });
  }

  // ---------------------------
  // ✅ Load logged-in user profile (users/{uid}) + admin claims
  // ---------------------------
  async function loadMe() {
    try {
      setMeLoading(true);
      setLoadErr("");
      setErr("");

      const u = auth.currentUser;
      if (!u) throw new Error("You must be signed in.");

      const tokenRes = await u.getIdTokenResult();
      const claims = tokenRes?.claims || {};
      const admin =
        claims.admin === true || String(claims.role || "") === "admin";
      setIsAdmin(!!admin);

      const snap = await getDoc(doc(db, "users", u.uid));
      if (!snap.exists())
        throw new Error("User profile not found (users/{uid}).");

      const data = snap.data() || {};
      // Deny-by-default: only recognised roles get behaviour; claims are a fallback
      // for the same normalisation, never a privilege source of their own.
      const cleanRole = normalizeRole(data?.role || claims.role);
      setMyRole(cleanRole);
      setMyTeamId(String(data?.teamId || "").trim());
      if (data?.disabled) {
        throw new Error("Your account is disabled. Please contact an admin.");
      }

      if (admin) {
        setMyOrgId("");
        return;
      }

      if (!data?.orgId)
        throw new Error("No organisation assigned to your account.");

      setMyOrgId(String(data.orgId));
    } catch (e) {
      setIsAdmin(false);
      setMyOrgId("");
      setMyRole("staff");
      setMyTeamId("");
      setLoadErr(e?.message || "Failed to load user profile");
    } finally {
      setMeLoading(false);
    }
  }

  useEffect(() => {
    loadMe();
  }, []);

  // ---------------------------
  // ✅ Load submission, staff base, client header
  // ---------------------------
  useEffect(() => {
    if (!submissionId) return;
    if (meLoading) return;
    if (loadErr) return;

    setErr("");
    setMsg("");
    setLoading(true);

    const subRef = doc(db, "kyc_submissions", submissionId);

    const unsub = onSnapshot(
      subRef,
      async (snap) => {
        try {
          if (!snap.exists()) {
            setLoadErr("Submission not found.");
            setLoading(false);
            return;
          }

          const sub = { id: snap.id, ...snap.data() };

          // ✅ ORG RESTRICTION
          const subOrgId = String(sub.orgId || "").trim();
          if (!isAdmin) {
            if (!myOrgId) {
              setLoadErr("Missing organisation. Access denied.");
              setLoading(false);
              return;
            }
            if (!subOrgId || subOrgId !== String(myOrgId)) {
              setLoadErr(
                "Access denied: you cannot review submissions from another organisation."
              );
              setLoading(false);
              return;
            }
          }

          setSubmission(sub);
          setOrgPreferredPhoneCountry("");
          setOrgJurisdiction(null);

          if (subOrgId) {
            const orgSnap = await getDoc(doc(db, "orgs", subOrgId));
            setOrgJurisdiction(orgSnap.exists() ? (orgSnap.data()?.geo || "uae") : null);
            setOrgPreferredPhoneCountry(
              orgSnap.exists() ? orgGeoToPreferredCountry(orgSnap.data()?.geo) : ""
            );
          }

          const cid = String(sub.clientId || "");
          if (!cid) {
            setLoadErr("Submission missing clientId.");
            setLoading(false);
            return;
          }

          // ✅ Load staff baseline KYC (kyc/{clientId})
          const staffRef = doc(db, "kyc", cid);
          const staffSnap = await getDoc(staffRef);
          const staffRaw = staffSnap.exists() ? staffSnap.data() : null;
          setStaffBase(staffRaw);

          // ✅ Load client header (clients/{clientId})
          const clientSnap = await getDoc(doc(db, "clients", cid));
          if (clientSnap.exists()) {
            const c = clientSnap.data() || {};
            const cAccessScope = String(c.accessScope || "").trim();
            const cOwnerUid = String(c.ownerUid || "").trim();
            const cTeamId = String(c.teamId || "").trim();
            const myUid = String(auth.currentUser?.uid || "").trim();
            if (
              !isAdmin &&
              myRole !== "manager" &&
              !(
                (cAccessScope === "team" && myTeamId && cTeamId === myTeamId) ||
                (cAccessScope === "individual" && cOwnerUid === myUid)
              )
            ) {
              setLoadErr("Access denied: this client belongs to another staff member.");
              setLoading(false);
              return;
            }
            setClientName(c.clientName || c.name || "");
            const t = String(c.type || c.clientType || c.customerType || "");
            setClientType(
              t.toLowerCase() === "entity" ? "entity" : "individual"
            );
          } else {
            setClientName(cid);
            setClientType("");
          }

          // ✅ Put submission data into form (So if the submission contains clientComment or any other, it will populate automatically.)

          // Document sent:

          // const sub = { id: snap.id, ...snap.data() };

          //   sub = {
          //   data: {
          //     customer: {...},
          //     sourceOfFundsWealth: {...},
          //     clientComment: "Hello, I left some blanks..."
          //   }
          // }

          // 1) ...prev
          // Starts from your default form structure (whatever you initialized in useState).

          // 2) ...data
          // Copies every top-level key from data into the form:
          // •	customer
          // •	ownership
          // •	clientComment (top-level) → gets added/overwrites prev.clientComment -> So at this moment, clientComment is already in the new form state.

          // 3) The nested merges after that
          // customer: { ...prev.customer, ...(data.customer || {}) },
          // => are there to make nested objects safe/complete (so you don’t lose defaults if submission data is partial).

          // ✅ Conclusion: If clientComment is top-level inside sub.data, it will be loaded into form.clientComment automatically due to ...data.

          // If you saved it as: data: { documents: { clientComments: "..." } }

          //  Then it will populate into:
          // 	•	form.documents.clientComments ✅ because you merge documents: { ...prev.documents, ...(data.documents||{}) }
          // …but it will not appear in form.clientComment (different path).

          //         Practical rule
          // •	If you store comment at top-level: use form.clientComment everywhere.
          // •	If you store comment under documents: use form.documents.clientComments everywhere.
          // Mixing both will look like “it didn’t load”, because you’re reading the wrong path.

          const data = normalizeClientKycData(sub.data || {});
          setForm((prev) => ({
            ...prev,
            ...data,
            engagement: { ...prev.engagement, ...(data.engagement || {}) },
            customer: { ...prev.customer, ...(data.customer || {}) },
            uaeCompany: { ...prev.uaeCompany, ...(data.uaeCompany || {}) },
            ownership: { ...prev.ownership, ...(data.ownership || {}) },
            // sourceOfFundsWealth: {
            //   ...prev.sourceOfFundsWealth,
            //   ...(data.sourceOfFundsWealth || {}),
            //   sourceOfWealth: Array.isArray(
            //     data.sourceOfFundsWealth?.sourceOfWealth
            //   )
            //     ? data.sourceOfFundsWealth.sourceOfWealth
            //     : [],
            // },
            sourceOfFundsWealth: {
              ...prev.sourceOfFundsWealth,
              ...(data.sourceOfFundsWealth || {}),

              // ✅ always store as array (SoW checkboxes)
              sourceOfWealth: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfWealth
              )
                ? data.sourceOfFundsWealth.sourceOfWealth
                : [],

              // ✅ always store as array (SoF MultiSelect)
              sourceOfFundsTypes: Array.isArray(
                data.sourceOfFundsWealth?.sourceOfFundsTypes
              )
                ? data.sourceOfFundsWealth.sourceOfFundsTypes
                : [],
            },
            // sourceOfFundsWealth: {
            //   ...prev.sourceOfFundsWealth,
            //   ...(data.sourceOfFundsWealth || {}),
            // },
            pepSanctions: {
              ...prev.pepSanctions,
              ...(data.pepSanctions || {}),
            },
            documents: { ...prev.documents, ...(data.documents || {}) },
            internalUse: { ...prev.internalUse, ...(data.internalUse || {}) },
          }));

          setLoading(false);
        } catch (e) {
          setLoadErr(e?.message || "Failed to load review page.");
          setLoading(false);
        }
      },
      (e) => {
        setLoadErr(e?.message || "Failed to subscribe to submission.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [submissionId, meLoading, isAdmin, myOrgId, myRole, myTeamId, loadErr]);

  // ---------------------------
  // Highlight logic (submission vs staff baseline)
  // ---------------------------
  const baseEditable = useMemo(
    () => pickClientEditable(staffBase),
    [staffBase]
  );
  const currentEditable = useMemo(() => pickClientEditable(form), [form]);

  function isChanged(path) {
    const a = getAt(baseEditable, path);
    const b = getAt(currentEditable, path);
    return !deepEqual(a, b);
  }

  function isArrayValueChanged(path, value) {
    const a = getAt(baseEditable, path);
    const b = getAt(currentEditable, path);
    const before = Array.isArray(a) ? a.includes(value) : false;
    const after = Array.isArray(b) ? b.includes(value) : false;
    return before !== after;
  }

  function changedClass(path) {
    return isChanged(path) ? "kycChanged" : "";
  }

  function changedRowIds(path) {
    const baseRows = getAt(baseEditable, path);
    const currentRows = getAt(currentEditable, path);
    if (!Array.isArray(currentRows)) return [];

    const baseByStableId = new Map();
    (Array.isArray(baseRows) ? baseRows : []).forEach((row, idx) => {
      const keys = [
        row?._rowId,
        row?.treeNodeId,
        row?.relatedShareholderRowId && row?.name
          ? `${row.relatedShareholderRowId}|${row.name}`
          : "",
        `idx_${idx}`,
      ]
        .map((v) => String(v || "").trim())
        .filter(Boolean);
      keys.forEach((key) => {
        if (!baseByStableId.has(key)) baseByStableId.set(key, row);
      });
    });

    return currentRows
      .map((row, idx) => {
        const rowId = String(row?._rowId || row?.treeNodeId || `idx_${idx}`);
        const keys = [
          row?._rowId,
          row?.treeNodeId,
          row?.relatedShareholderRowId && row?.name
            ? `${row.relatedShareholderRowId}|${row.name}`
            : "",
          `idx_${idx}`,
        ]
          .map((v) => String(v || "").trim())
          .filter(Boolean);
        const baseRow = keys.map((key) => baseByStableId.get(key)).find(Boolean);
        return !baseRow || !deepEqual(baseRow, row) ? rowId : "";
      })
      .filter(Boolean);
  }

  const ownershipChangedRows = useMemo(
    () => ({
      shareholders: changedRowIds(["ownership", "shareholders"]),
      ubos: changedRowIds(["ownership", "ubos"]),
    }),
    [baseEditable, currentEditable]
  );

  const signatoryChangedRows = useMemo(
    () => changedRowIds(["ownership", "signatories"]),
    [baseEditable, currentEditable]
  );

  const isApproved =
    String(submission?.status || "").toLowerCase() === "approved";

  // The organisation's name entry setting (one full name, or given names + surname).
  const { mode: individualNameMode } = useNameEntryMode(form?.orgId || myOrgId);

  // Re-run the UBO assessment after ownership edits, so the results shown are never outdated.
  const { failure: liveUboCheckFailure } = useLiveUboAssessment({ form, setForm, orgJurisdiction, enabled: !loading && !isApproved });

  // ---------------------------
  // ✅ Approve (sync + DELETE submission)
  // ---------------------------
  async function doApprove() {
    try {
      setErr("");
      setMsg("");
      setSaving(true);

      const cid = String(submission.clientId);
      const subRef = doc(db, "kyc_submissions", submission.id);
      const kycRef = doc(db, "kyc", cid);
      const clientRef = doc(db, "clients", cid);

      const accountHolderName =
        String(form.customer?.name || "").trim() || "Account holder";
      const liveOwnershipGraph = buildLiveReviewOwnershipSnapshot({
        ownership: form.ownership || {},
        accountHolderName,
      });

      // Assess UBO thresholds before writing to Firestore using the same
      // live graph that the reviewer sees on the review page.
      let ownershipForWrite = {
        ...(form.ownership || {}),
        assistantTree: liveOwnershipGraph?.mermaid
          ? liveOwnershipGraph
          : form.ownership?.assistantTree || null,
      };
      try {
        const uboApiPayload = buildReviewOwnershipApiPayload({
          ownership: form.ownership || {},
          accountHolderName,
          liveOwnershipGraph,
        });

        if (uboApiPayload.initialAHType && uboApiPayload.nodes.length > 0) {
          const apiResult = await assessUbosFromTree(
            uboApiPayload.initialAHType,
            uboApiPayload.nodes,
            orgJurisdiction
          );
          ownershipForWrite = applyUboAssessmentResultToOwnership({
            ownership: ownershipForWrite,
            apiPayload: uboApiPayload,
            apiResult,
            signature: "",
          });
        }
      } catch (uboErr) {
        // Never approve with UBO results that may predate the client's changes (all jurisdictions).
        throw new Error(`UBO threshold assessment failed: ${uboErr.message}. Nothing was approved; please try again.`);
      }

      const batch = writeBatch(db);
      const normalizedForm = removeUndefinedDeep(
        normalizeClientKycData({
          ...form,
          ownership: ownershipForWrite,
        })
      );

      batch.set(
        kycRef,
        {
          ...normalizedForm,
          clientId: cid,
          updatedAt: serverTimestamp(),
          lastApprovedSubmissionId: submission.id,
          lastApprovedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const approvedName = String(normalizedForm?.customer?.name || "").trim();
      const approvedType = String(normalizedForm?.customer?.type || "").toLowerCase();
      const approvedAddress = String(normalizedForm?.customer?.address || "").trim();

      const clientUpdate = {
        kycFlowStage: "approved",
        kycLastSubmissionId: submission.id,
        kycLastSubmissionAt: serverTimestamp(),
      };

      if (approvedName) clientUpdate.clientName = approvedName;
      if (approvedType === "entity" || approvedType === "individual") {
        clientUpdate.type = approvedType;
      }
      if (approvedAddress) clientUpdate.address = approvedAddress;

      batch.set(clientRef, clientUpdate, { merge: true });

      // ✅ delete submission
      batch.delete(subRef);

      await batch.commit();

      if (onSuccess) {
        onSuccess({ severity: "success", summary: "Approved", detail: `Submission approved. KYC updated for ${clientName || cid}.`, life: 2500 });
      } else {
        nav(`/kyc/${cid}`, {
          replace: true,
          state: {
            toastId: crypto?.randomUUID?.() || String(Date.now()),
            toast: { severity: "success", summary: "Approved", detail: `Submission approved. KYC updated for ${clientName || cid}.`, life: 2500 },
          },
        });
      }
    } catch (e) {
      setErr(e?.message || "Approve failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (!submission || !submission.clientId) return;
    if (saving) return;

    appConfirm({
      group: "appConfirm",
      header: "Approve submission",
      message:
        "Approve this submission and sync it into the official KYC dossier?",
      defaultFocus: "reject",
      acceptLabel: "Approve",
      rejectLabel: "Cancel",
      scheme: "success",
      icon: "bi bi-check2-circle",
      accept: () => doApprove(),
    });
  }

  // ---------------------------
  // ✅ Delete submission (reset workflow)
  // ---------------------------
  async function doRejectDelete() {
    try {
      setErr("");
      setMsg("");
      setSaving(true);

      const cid = String(submission.clientId);
      const subRef = doc(db, "kyc_submissions", submission.id);
      const clientRef = doc(db, "clients", cid);

      const batch = writeBatch(db);

      batch.delete(subRef);

      batch.set(
        clientRef,
        {
          kycFlowStage: "not_sent",
          kycInviteSentAt: null,
          kycLastSubmissionId: null,
          kycLastSubmissionAt: null,
        },
        { merge: true }
      );

      await batch.commit();

      if (onSuccess) {
        onSuccess({ severity: "warn", summary: "Deleted", detail: `Submission deleted for ${clientName || cid}.`, life: 3000 });
      } else {
        nav(backTo, {
          replace: true,
          state: {
            toastId: crypto?.randomUUID?.() || String(Date.now()),
            toast: { severity: "warn", summary: "Deleted", detail: `Submission deleted for ${clientName || cid}.`, life: 3000 },
          },
        });
      }
    } catch (e) {
      setErr(e?.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleRejectDelete() {
    if (!submission || !submission.clientId) return;

    appConfirm({
      group: "appConfirm",
      header: "Delete submission",
      message:
        "This will DELETE the submitted form and reset the KYC workflow.",
      defaultFocus: "reject",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-trash",
      accept: () => doRejectDelete(),
    });
  }

  // ---------------------------
  // ✅ Reject & Resend
  // ---------------------------
  async function doRejectResend() {
    try {
      setErr("");
      setMsg("");
      setSaving(true);

      const u = auth.currentUser;
      if (!u) throw new Error("You must be signed in.");

      const cid = String(submission?.clientId || "").trim();
      if (!cid) throw new Error("Missing clientId in submission.");

      let resendEmail = String(
        submission?.inviteEmail ||
          submission?.clientEmail ||
          submission?.data?.customer?.email ||
          ""
      )
        .trim()
        .toLowerCase();

      if (!isValidEmail(resendEmail)) {
        const manual = window
          .prompt(
            "Client email was not found in this submission. Please enter the client email to resend the KYC link:",
            ""
          )
          ?.trim()
          .toLowerCase();

        if (!manual) throw new Error("Resend cancelled (no email provided).");
        if (!isValidEmail(manual)) throw new Error("Invalid email entered.");
        resendEmail = manual;
      }

      const idToken = await u.getIdToken();

      const res = await fetch(joinUrl(SERVER_URL, "/myKyc/staff/kyc-link/send"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          clientId: cid,
          email: resendEmail,
          mode: "edit_requested",
          submissionId: submission.id,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok !== true) {
        throw new Error(json?.error || "Reject & resend failed");
      }

      if (onSuccess) {
        onSuccess({ severity: "warn", summary: "Rejected & resent", detail: `Client invited again to amend the form for ${clientName || cid}.`, life: 3500 });
      } else {
        nav(backTo, {
          replace: true,
          state: {
            toastId: crypto?.randomUUID?.() || String(Date.now()),
            toast: { severity: "warn", summary: "Rejected & resent", detail: `Client invited again to amend the form for ${clientName || cid}.`, life: 3500 },
          },
        });
      }
    } catch (e) {
      setErr(e?.message || "Reject & resend failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleRejectResend() {
    if (!submission || !submission.clientId) return;

    appConfirm({
      group: "appConfirm",
      header: "Reject & resend to client",
      message:
        "Reject this submission and resend a new secure link to the client so they can amend the form?",
      defaultFocus: "reject",
      acceptLabel: "Reject & resend",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-envelope-exclamation",
      accept: () => doRejectResend(),
    });
  }

  // -------------------------
  // Render loading
  // -------------------------
  if (loading || meLoading) {
    if (isModal) return <div className="kycReviewModalLoadingState"><div className="appDotsLoader" /></div>;
    return null;
  }

  // ✅ fatal
  if (loadErr) {
    return (
      <motion.div
        variants={pageAnim}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.25 }}
        className="KycClientSideFormPage kycReviewPage"
      >
        {!isModal && (
          <div className="kycTitleBar">
            <div className="container py-3">
              <div className="kycHeaderTitle">Submitted KYC Form Review</div>
              <div className="kycHeaderSub">Access error</div>
            </div>
          </div>
        )}

        <div className="container pb-4">
          <div className="alert alert-danger" style={{ marginTop: 20 }}>
            {loadErr}
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn kycBtnOutline kycReviewBackBtn"
              type="button"
              onClick={() => onClose ? onClose() : nav(backTo)}
              aria-label={backLabel}
              title={backLabel}
            >
              <img src={iconReturnWhite} alt="" className="kycReviewBackIcon" aria-hidden="true" />
              <img src={iconMailboxD} alt="" className="kycReviewBackIcon" aria-hidden="true" />
            </button>
            <button
              className="btn kycBtnPrimary"
              type="button"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  const submittedAtText = submission?.submittedAt?.toDate
    ? submission.submittedAt.toDate().toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const typeLower = String(clientType || "").toLowerCase();
  const reviewActionGroup = (
    <div
      className={`kycReviewActionGroup${isModal ? " kycReviewActionGroup--modal" : ""}`}
      role="group"
      aria-label="KYC review actions"
    >
      <button
        className="btn kycReviewActionBtn kycReviewActionResend"
        type="button"
        disabled={saving || isApproved}
        onClick={handleRejectResend}
      >
        <img src={iconThumbsDownB} alt="" className="kycReviewActionIcon" aria-hidden="true" />
        <span>Reject &amp; Resend</span>
      </button>

      <button
        className="btn kycReviewActionBtn kycReviewActionDelete"
        type="button"
        disabled={saving || isApproved}
        onClick={handleRejectDelete}
      >
        <img src={iconTrashBin} alt="" className="kycReviewActionIcon" aria-hidden="true" />
        <span>Delete</span>
      </button>

      <button
        className="btn kycReviewActionBtn kycReviewActionApprove"
        type="button"
        disabled={saving || isApproved}
        onClick={handleApprove}
      >
        <img src={iconThumbsUpB} alt="" className="kycReviewActionIcon" aria-hidden="true" />
        <span>{saving ? "Approving…" : isApproved ? "Approved" : "Approve"}</span>
      </button>
    </div>
  );

  return (
    <OwnershipLabelsProvider jurisdiction={orgJurisdiction}>
    <motion.div
      variants={pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className={`KycClientSideFormPage kycReviewPage${isModal ? " kycReviewPage--modal" : ""}`}
    >
      {!isModal && (
        <div className="kycTitleBar">
          <div className="kycPageContainer py-3">
            <div className="d-flex justify-content-between align-items-center gap-3">
              <div className="min-w-0">
                <div className="kycHeaderTitle">Submitted KYC Form Review</div>

                <div className="kycHeaderSub">
                  {clientName ? (
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <span className="text-truncate">{clientName}</span>
                      {clientType && (
                        <span
                          className={
                            "kycTypeBadge " +
                            (clientType === "entity"
                              ? "kycTypeBadgeEntity"
                              : "kycTypeBadgeIndividual")
                          }
                        >
                          {typeLower === "entity" ? "Entity" : "Individual"}
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              <button
                className="btn kycBackBtn kycReviewBackBtn"
                type="button"
                disabled={saving}
                onClick={() => nav(backTo)}
                aria-label={backLabel}
                title={backLabel}
              >
                <img src={iconReturnWhite} alt="" className="kycReviewBackIcon" aria-hidden="true" />
                <img src={iconMailboxD} alt="" className="kycReviewBackIcon" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="kycPageContainer pb-4">
        <div className="kycTopActions">
          <div className="kycMeta kycReviewSubmittedMeta">
            <div className="text-muted small">
              KYC submitted by client on {submittedAtText}
            </div>
          </div>

          {!isModal && (
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn kycBtnOutline"
                type="button"
                disabled={saving || isApproved}
                onClick={handleRejectResend}
              >
                <i
                  className="bi bi-envelope-exclamation me-2"
                  aria-hidden="true"
                />
                Reject & resend
              </button>

              <button
                className="btn kycBtnReject"
                type="button"
                disabled={saving || isApproved}
                onClick={handleRejectDelete}
              >
                <i className="bi bi-trash me-2" aria-hidden="true" />
                Delete
              </button>

              <button
                className="btn kycBtnApprove"
                type="button"
                disabled={saving || isApproved}
                onClick={handleApprove}
              >
                <i className="bi bi-save me-2" aria-hidden="true" />
                {saving ? "Approving…" : isApproved ? "Approved" : "Approve"}
              </button>
            </div>
          )}
        </div>

        {/* ✅ non-fatal */}
        {err && <div className="alert alert-danger">{err}</div>}
        {msg && <div className="alert alert-success">{msg}</div>}

        {/* ✅ Component-based review sections */}
        <KycSectionCustomerDetails
          form={form}
          mode="review"
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={saving || isApproved}
          sectionNo={sectionNo.customerDetails}
          preferredPhoneCountry={orgPreferredPhoneCountry}
        />

        {isEntity && liveUboCheckFailure ? (
          <div role="status" style={{ fontSize: 13, color: "#9B2C2C", margin: "0 0 8px" }}>
            UBO check failed: {liveUboCheckFailure} It runs again at the next ownership change.
          </div>
        ) : null}

        {isEntity ? (
          <KycReviewOwnershipSection
            orgJurisdiction={orgJurisdiction}
            form={form}
            onSetAt={setAt}
            changedClass={changedClass}
            changedRows={ownershipChangedRows}
            readOnly={saving || isApproved}
            sectionNo={sectionNo.ownership}
            separateNames={individualNameMode === "separate"}
          />
        ) : null}

        {isEntity ? (
          <KycClientSignatoriesDesktop
            form={form}
            onSetAt={setAt}
            changedClass={changedClass}
            changedRowIds={signatoryChangedRows}
            readOnly={saving || isApproved}
            sectionNo={sectionNo.signatories}
          />
        ) : null}

        {/* Optional SOW/SOF section */}

        {/* <KycSectionOwnership
          form={form}
          mode="review"
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={false}
        /> */}

        {/* <KycSectionOwnership
          form={form}
          mode="staff"
          onSetAt={setAt}
          changedClass={changedClass}
        /> */}

        <KycSectionSowSof
          orgJurisdiction={orgJurisdiction}
          form={form}
          mode="review"
          onSetAt={setAt}
          changedClass={changedClass}
          changedArrayValue={isArrayValueChanged}
          readOnly={false}
          sectionNo={sectionNo.sowSof}
        />

        {/* <KycSectionSowSof
          form={form}
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={saving || isApproved}
        /> */}

        <KycSectionPepSanctions
          readOnly={isReadOnlyArea(myRole, PROTECTED_AREAS.SCREENING)}
          form={form}
          mode="review"
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={false}
          sectionNo={sectionNo.pepSanctions}
        />

        {String(form.clientComment || "").trim() ? (
          <div className="card kycCard mb-3">
            <div className="kycSectionHead">
              <h5 className="kycSectionTitle">
                {sectionNo.pepSanctions + 1} | Client comments
              </h5>
              {/* <div className="kycSectionHint">Provided by the client.</div> */}
            </div>
            <div className="kycSectionBody">
              <div style={{ whiteSpace: "pre-wrap" }}>
                {String(form.clientComment).trim()}
              </div>
            </div>
          </div>
        ) : null}

        {/* <KycSectionPepSanctions
          form={form}
          onSetAt={setAt}
          changedClass={changedClass}
          readOnly={saving || isApproved}
        /> */}

      </div>
      {isModal && (
        <div className="kycReviewActionDock">
          {reviewActionGroup}
        </div>
      )}
    </motion.div>
    </OwnershipLabelsProvider>
  );
}
